import { useState } from 'react';
import { Link } from 'react-router';
import { ApiError, type components } from '@awj/api-client';
import { formatDate, formatDateTime } from '@awj/i18n';
import { Button, Dialog, Skeleton, useToast } from '@awj/ui';

import { ExportMenu } from '~/components/ExportMenu.tsx';
import { useI18n } from '~/i18n/index.tsx';
import { staffColumns } from '~/lib/export-columns.ts';
import { useChangeStaffRole, useSetStaffBlocked, useStaff } from '~/lib/settings-queries.ts';
import { useSession } from '~/lib/session.tsx';

type Member = components['schemas']['StaffMemberResponse'];

/** A change to someone's access that waits for a second click. */
type Pending = { member: Member; action: 'promote' | 'demote' | 'block' };

/**
 * Who can get into the back office.
 *
 * Your own row offers nothing: the server refuses it anyway, and a button that exists to be
 * refused is worse than none.
 *
 * Inviting somebody happens on {@link StaffInvitePage}, at a URL of its own.
 */
export function StaffPanel() {
  const { t, locale } = useI18n();
  const toast = useToast();
  const { profile } = useSession();

  const staff = useStaff();
  const changeRole = useChangeStaffRole();
  const setBlocked = useSetStaffBlocked();

  // Promoting, demoting and blocking a colleague each change what they can do the moment they
  // happen, so each is confirmed — as blocking a customer already was. Unblocking is not: it only
  // gives back what they had.
  const [pending, setPending] = useState<Pending | null>(null);

  async function changeRoleOf(member: Member) {
    try {
      await changeRole.mutateAsync({
        id: member.id,
        role: member.role === 'SuperAdmin' ? 'Admin' : 'SuperAdmin',
      });
      toast.show({ title: t('settings.roleChanged'), tone: 'success' });
    } catch (caught) {
      report(caught, t('settings.couldNotChangeRole'));
    }
  }

  async function setBlockedOf(member: Member, blocked: boolean) {
    try {
      await setBlocked.mutateAsync({ id: member.id, blocked });
      toast.show({
        title: blocked ? t('customers.blockedNow') : t('customers.unblocked'),
        tone: 'success',
      });
    } catch (caught) {
      report(caught, t('customers.couldNotChange'));
    }
  }

  async function confirm() {
    if (!pending) {
      return;
    }

    if (pending.action === 'block') {
      await setBlockedOf(pending.member, true);
    } else {
      await changeRoleOf(pending.member);
    }

    setPending(null);
  }

  function report(caught: unknown, fallback: string) {
    toast.show({
      title: fallback,
      message: caught instanceof ApiError ? caught.problem.detail : undefined,
      tone: 'danger',
    });
  }

  if (staff.isPending) {
    return <Skeleton height="260px" />;
  }

  return (
    <>
      <div className="panel-head">
        <p className="panel-head__lede">{t('settings.staffLede')}</p>
        <div className="panel-head__actions">
          <ExportMenu
            file="staff"
            sheet={t('settings.staff')}
            columns={staffColumns(t)}
            rows={() => staff.data ?? []}
            disabled={!staff.data?.length}
          />
          <Link className="awj-button awj-button--primary awj-button--sm" to="/settings/staff/invite">
            {t('settings.invite')}
          </Link>
        </div>
      </div>

      <div className="data-table">
  <div className="data-table__scroll" tabIndex={0} role="region" aria-label={t('settings.staff')}>
          <table className="data-table__table">
            <thead>
              <tr>
                <th className="data-table__th" scope="col">{t('settings.person')}</th>
                <th className="data-table__th" scope="col">{t('settings.role')}</th>
                <th className="data-table__th" scope="col">{t('settings.added')}</th>
                <th className="data-table__th" scope="col">{t('settings.lastSignIn')}</th>
                <th scope="col" className="data-table__th data-table__cell--end">
                  <span className="sr-only">{t('common.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {(staff.data ?? []).map((member: Member) => {
                const isMe = member.id === profile?.id;

                return (
                  <tr key={member.id}>
                    <td className="data-table__td">
                      <bdi>{member.fullName || member.email}</bdi>
                      <span className="data-table__sub">
                        <bdi>{member.email}</bdi>
                      </span>
                      {member.isBlocked ? (
                        <span className="status status--cancelled">{t('customers.blocked')}</span>
                      ) : null}
                    </td>
                    <td className="data-table__td">
                      <span className="status status--inquiry-new">
                        {t(`role.${member.role}` as 'role.Admin')}
                      </span>
                    </td>
                    <td className="data-table__td">
                      <bdi>{formatDate(locale, member.createdAt)}</bdi>
                    </td>
                    <td className="data-table__td">
                      {member.lastSignInAt ? (
                        <bdi>{formatDateTime(locale, member.lastSignInAt)}</bdi>
                      ) : (
                        <span className="data-table__sub">{t('settings.neverSignedIn')}</span>
                      )}
                    </td>
                    <td className="data-table__td data-table__cell--end">
                      {isMe ? (
                        // Nothing here for your own row: you cannot demote or block yourself, and
                        // the server would refuse it.
                        <span className="data-table__sub">{t('settings.thisIsYou')}</span>
                      ) : (
                        /*
                         * Words, not glyphs: no icon says "make this person a super admin". They get
                         * an outline instead, because promoting or blocking a colleague should look
                         * like pressing a control rather than following a link.
                         */
                        <div className="row-actions">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            // This row's request, not the shared mutation's: `isPending` alone
                            // put every row's buttons into a spinner while one person changed.
                            loading={changeRole.isPending && changeRole.variables?.id === member.id}
                            loadingLabel={t('common.saving')}
                            onClick={() =>
                              setPending({ member, action: member.role === 'SuperAdmin' ? 'demote' : 'promote' })
                            }
                          >
                            {member.role === 'SuperAdmin' ? t('settings.demote') : t('settings.promote')}
                          </Button>

                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className={member.isBlocked ? undefined : 'awj-button--quietDanger'}
                            loading={setBlocked.isPending && setBlocked.variables?.id === member.id}
                            loadingLabel={t('common.saving')}
                            onClick={() =>
                              member.isBlocked
                                ? setBlockedOf(member, false)
                                : setPending({ member, action: 'block' })
                            }
                          >
                            {member.isBlocked ? t('customers.unblock') : t('customers.block')}
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
</div>

      <Dialog
        open={pending !== null}
        onClose={() => setPending(null)}
        title={
          pending
            ? t(`settings.confirm.${pending.action}Title` as 'settings.confirm.blockTitle', {
                name: pending.member.fullName || pending.member.email,
              })
            : ''
        }
        closeLabel={t('common.close')}
        size="sm"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setPending(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant={pending?.action === 'block' ? 'danger' : 'primary'}
              loading={changeRole.isPending || setBlocked.isPending}
              onClick={confirm}
            >
              {pending?.action === 'block'
                ? t('customers.block')
                : pending?.action === 'demote'
                  ? t('settings.demote')
                  : t('settings.promote')}
            </Button>
          </>
        }
      >
        {pending ? (
          <p className="dialog-lede">
            {t(`settings.confirm.${pending.action}Body` as 'settings.confirm.blockBody')}
          </p>
        ) : null}
      </Dialog>
    </>
  );
}
